// 1. Import necessary components
import { ref } from 'vue';
import { App } from '@capacitor/app';
import appVersionService from '@/shared/services/app-version.service';
import {
  getAppInfo,
  getAppPlatform,
  compareAppVersions,
  resolveUpdateUrl,
} from '@/shared/utilities/app-version';

// 2. Use
const VERSION_RECHECK_THROTTLE_MS = 30 * 60 * 1000;

// Gate state lives at module scope so consumers share one gate:
// the check runs once per trigger no matter how many components use it. Nothing
// is persisted; killing the app resets everything so cold start always checks.
const isUpdateRequired = ref(false);
const hasVersionCheckFailed = ref(false);
const isCheckingVersion = ref(false);
const hasCompletedInitialCheck = ref(false);
const updateUrl = ref('');

let lastCheckTimestamp = 0;
let gateStarted = false;
let wentToBackground = false;
let checkPromise = null;

export const shouldCheckVersionOnForeground = ({ nowTimestamp, lastCheck }) =>
  nowTimestamp - lastCheck >= VERSION_RECHECK_THROTTLE_MS;

const runVersionCheck = async () => {
  const platform = getAppPlatform();

  // Only installed iOS and Android applications have supported store targets.
  if (platform !== 'ios' && platform !== 'android') return;

  try {
    const appInfo = await getAppInfo();
    const destination = resolveUpdateUrl({ platform, appId: appInfo.id });

    // Do not request or enforce a minimum version when this build has no valid
    // store destination. Blocking without somewhere to update would trap users.
    if (!destination) return;

    const version = await appVersionService.getVersion();
    const minimumVersion = platform === 'ios' ? version.minVersionIos : version.minVersionAndroid;

    const versionComparison = compareAppVersions(appInfo.version, minimumVersion);
    if (versionComparison === null) {
      hasVersionCheckFailed.value = true;
      return;
    }

    // Only a valid comparison re-arms the 30-minute foreground throttle.
    lastCheckTimestamp = Date.now();
    hasVersionCheckFailed.value = false;

    if (versionComparison >= 0) {
      isUpdateRequired.value = false;
      updateUrl.value = '';
      return;
    }

    // The root dialog retains the update requirement across route changes.
    updateUrl.value = destination;
    isUpdateRequired.value = true;
  } catch {
    // Keep the version dialog available for an explicit retry. A failed request
    // cannot confirm support or clear an already confirmed update requirement.
    hasVersionCheckFailed.value = true;
  }
};

const checkAppVersion = () => {
  // Share one request: concurrent triggers (cold start racing a foreground
  // return) must not send duplicate version requests.
  if (!checkPromise) {
    isCheckingVersion.value = true;

    checkPromise = runVersionCheck().finally(() => {
      isCheckingVersion.value = false;
      hasCompletedInitialCheck.value = true;
      checkPromise = null;
    });
  }
  return checkPromise;
};

const handleAppStateChange = (state) => {
  if (!state.isActive) return;

  // Notification shade and permission dialogs also toggle the active flag.
  // Only a return from a real backgrounding may check; consume the arming
  // on every activation so an earlier pause cannot leak into a later toggle.
  const wasBackgrounded = wentToBackground;
  wentToBackground = false;

  if (!wasBackgrounded) return;

  const shouldCheck = shouldCheckVersionOnForeground({
    nowTimestamp: Date.now(),
    lastCheck: lastCheckTimestamp,
  });
  if (!shouldCheck) return;

  void checkAppVersion();
};

export function useAppVersionGate() {
  // 3. Define a reactive variable

  // 4. Define methods or computed properties if needed
  // 4.1 Define methods
  const startVersionGate = () => {
    // Start once per app process. Repeated component calls share
    // the in-flight request and do not register duplicate native listeners.
    if (gateStarted) return checkPromise ?? Promise.resolve();
    gateStarted = true;

    if (getAppPlatform() === 'web') return Promise.resolve();

    // The pause listener arms real background returns; shade pulls and
    // permission dialogs toggle the active flag without backgrounding.
    void App.addListener('appStateChange', handleAppStateChange);
    void App.addListener('pause', () => {
      wentToBackground = true;
    });

    // Check after app startup, then again on eligible foreground returns.
    return checkAppVersion();
  };

  const checkAppVersionNow = async () => {
    if (!gateStarted) return startVersionGate();

    // Finish the earlier request before checking the new session or connection.
    // Callers waiting together share the fresh request through checkAppVersion.
    await checkPromise;

    return checkAppVersion();
  };

  // 4.2 Define computed properties

  // 5. VueJS Hooks

  return {
    isUpdateRequired,
    hasVersionCheckFailed,
    isCheckingVersion,
    hasCompletedInitialCheck,
    updateUrl,
    startVersionGate,
    checkAppVersionNow,
  };
}

export default useAppVersionGate;
